import React, { useState, useEffect } from "react";
import { Form, InputNumber, Input, Button, Checkbox, Row, Col, Cascader, DatePicker, Typography, Table, Select } from "antd";
import TableSelector from "../TableSelector/TableSelector";
import axios from "axios";
import dayjs from 'dayjs';
import i18n from "i18next";
import { useTranslation } from "react-i18next";

const { Title } = Typography;
const { TextArea } = Input;

const SurveysDamageForm = props => {
  const [form] = Form.useForm();
  const [causadByDamages, setCausadByDamages] = useState([]);
  const [causedByLoaderState, setCausedByLoaderState] = useState(false);
  const isEditMode = props.isEditMode || false;
  const { t } = useTranslation();

  const formItemLayout = {
    labelCol: {
      sm: { span: 4 }
    },
    wrapperCol: {
      sm: { span: 20 }
    }
  };

  useEffect(() => {
    if (props.record.damage && props.eventId) {
      loadCausedByDamages();
      
      form.setFieldsValue({
        asset: props.record.damage.componentId ? [props.record.damage.componentId] : undefined,
        description: props.record.damage.description,
        date: props.record.damage.date ? dayjs(props.record.damage.date) : undefined,
        sendai: props.record.damage.sendaiIndicator,
        causedBy: props.record.damage.causedByDamageIds
      });
    } else {
      form.resetFields();
    }
  }, [props.record.damage, props.eventId, form]);

  // Reset validation when exiting edit mode
  useEffect(() => {
    if (!isEditMode) {
      form.resetFields();

      if (props.record.damage) {
        form.setFieldsValue({
          asset: props.record.damage.componentId ? [props.record.damage.componentId] : undefined,
          description: props.record.damage.description,
          date: props.record.damage.date ? dayjs(props.record.damage.date) : undefined,
          sendai: props.record.damage.sendaiIndicator,
          causedBy: props.record.damage.causedByDamageIds
        });
      }
    }
  }, [isEditMode, form, props.record.damage]);

  const loadCausedByDamages = () => {
    setCausedByLoaderState(true);
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/damages/event", {
        params: {
          id: props.eventId
        }
      })
      .then(({ data }) => {
        setCausadByDamages(data.data || []);
      })
      .catch(function (error) {
        console.log(error);
      })
      .finally(function () {
        setCausedByLoaderState(false);
      });
  };

  // Handle form changes and notify parent
  const handleFormChange = () => {
    const values = form.getFieldsValue();

    // Only check validation in edit mode
    let isValid = true;
    if (isEditMode) {
      // Check for existing errors without triggering new validation
      const errors = form.getFieldsError();
      const hasErrors = errors.some(error => error.errors.length > 0);

      // Check for required empty fields
      const requiredFields = ['asset', 'description'];
      const hasEmptyRequired = requiredFields.some(field => {
        if (field === 'asset') {
          return !values[field] || values[field].length === 0;
        }
        return !values[field];
      });

      isValid = !hasErrors && !hasEmptyRequired;
    }

    // Prepare damage object
    const newDamage = { ...props.record };
    newDamage.damage.componentId = values.asset ? values.asset[0] : undefined;
    newDamage.damage.description = values.description;
    newDamage.damage.date = values.date ? values.date.format('YYYY-MM-DD') : undefined;
    newDamage.damage.sendaiIndicator = values.sendai || null;
    newDamage.damage.causedByDamageIds = [].concat(values.causedBy || []);
    newDamage.valid = isValid;
    
    props.parentCallback(newDamage);
  };

  // Look up "componentType.<name>" or "enterpriseType.<value>" in
  // translation.json; fall back to the raw string if no key registered.
  // Identical pattern to AssetInfo.translateTypeName.
  const translateKey = (ns, raw) => {
    if (!raw) return raw;
    const key = `${ns}.${raw}`;
    return i18n.exists(key) ? t(key) : raw;
  };

  const causedByColumns = [
    {
      title: "Type",
      dataIndex: "enterpriseType",
      render: (text) => translateKey('enterpriseType', text),
    },
    {
      title: "Asset name",
      dataIndex: "componentName"
    },
    {
      title: "Asset Type",
      dataIndex: "componentTypeDescriptionPath",
      // May be a slash-joined path; translate each segment independently.
      render: (text) => text
        ? text.split('/').map(seg => translateKey('componentType', seg.trim())).join(' / ')
        : text,
    },
    {
      title: "Damage",
      dataIndex: "description"
    }
  ];

  return (
    <>
      <Form 
        form={form} 
        {...formItemLayout} 
        className="details_form"
        onValuesChange={handleFormChange}
        onFieldsChange={handleFormChange}
      >
        <Title level={5}>Damaged Component</Title>
        <Form.Item 
          label="Asset" 
          name="asset"
          rules={isEditMode ? [{ type: "array", required: true, message: "Please select an asset!" }] : []}
        >
          <Cascader
            options={props.assets.data}
            placeholder="Choose the damaged asset"
            disabled={!isEditMode}
          />
        </Form.Item>
        <Form.Item 
          label="Description" 
          name="description"
          rules={isEditMode ? [{ required: true, message: "Please input Description!" }] : []}
        >
          <TextArea rows={4} placeholder="Describe the damage" disabled={!isEditMode} />
        </Form.Item>
        <Form.Item
          label="Damage Date"
          name="date"
        >
          <DatePicker disabled={!isEditMode} />
        </Form.Item>
        <Form.Item
          label="Sendai indicator"
          name="sendai"
        >
          <Select
            disabled={!isEditMode}
            allowClear
            placeholder="Select a Sendai indicator"
            options={(props.sendaiTypes || []).map(code => ({ value: code, label: t('damageEnums.sendaiIndicator.' + code, code) }))}
          />
        </Form.Item>
        <Form.Item
          label="Caused by"
          name="causedBy"
          labelCol={{ span: 4 }}
        >
          <TableSelector
            columns={causedByColumns}
            dataSource={causadByDamages}
            loading={causedByLoaderState}
            disabled={!isEditMode}
          />
        </Form.Item>
      </Form>
    </>
  );
};

export default SurveysDamageForm;